import { useEffect, useRef } from 'react';
import axios, { AxiosResponse, AxiosInstance } from 'axios';
import ENV from 'environment';
import { find, trim } from 'lodash';
import { useAuth, AUTH_BEARER_KEY } from './useAuth';
import base64 from 'base-64';

type GraphQueryTemplate = (...a: any) => string;
type GraphQuery = string | GraphQueryTemplate;
type GraphArg = object | number | string;
export interface GraphAxios extends AxiosInstance {
    graph(query: GraphQuery, ...args: GraphArg[]): Promise<AxiosResponse<any, any>>;
    getUniqueToken(data: any): Promise<AxiosResponse<any, any>>;
}

// @ts-ignore
export function q(strings, ...keys): GraphQueryTemplate {
    const processingFn: any = function processing(...values: any) {
        const dict = values[values.length - 1] || {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const result = [this.strings[0]];

        // Helper function to determine if value is an enum
        function isEnumValue(value: any): boolean {
            return typeof value === 'string' && /^_(.+)_$/.test(value);
        }

        function cleanEnum(value: string): string {
            return value.replace(/^_(.+)_$/, '$1'); // Remove surrounding underscores
        }
        // @ts-ignore
        this.keys.forEach((key, i) => {
            const value = Number.isInteger(key) ? values[key] : dict[key];
            // @ts-ignore
            if (value === undefined && this.defaultValues?.[key]) {
                // @ts-ignore
                result.push(this.defaultValues[key], strings[i + 1]);
            } else {
                if (typeof value === 'object' && !Array.isArray(value)) {
                    // Convert object to GraphQL params
                    let data = trim(JSON.stringify(value), '{}');
                    data = data.replace(/"(\w+)":/g, '$1:'); // Remove quotes around object keys
                    result.push(data, strings[i + 1]);
                } else if (Array.isArray(value)) {
                    // Convert arrays while preserving enums
                    const formattedArray = value.map((v) => (isEnumValue(v) ? cleanEnum(v) : JSON.stringify(v)));
                    result.push(`[${formattedArray.join(', ')}]`, strings[i + 1]);
                } else {
                    // Check if it's an enum and avoid wrapping it in quotes
                    result.push(isEnumValue(value) ? cleanEnum(value) : JSON.stringify(value), strings[i + 1]);
                }
            }
        });
        return result.join('');
    };
    processingFn.strings = strings;
    processingFn.keys = keys;
    processingFn.defaultValues = {};
    return processingFn;
}
export function qAddDefaultValues(queryTemplate: any, map: any): GraphQueryTemplate {
    queryTemplate.defaultValues = Object.assign({}, queryTemplate.defaultValues, map);
    return queryTemplate;
}

// @ts-ignore
const Graph = function (query: GraphQuery, ...args: GraphArg[]): Promise<AxiosResponse<any, any>> {
    let qString: string = '';
    if (typeof query === 'string') {
        qString = query;
    } else if (typeof query === 'function') {
        qString = query.apply(query, args);
    }
    // @ts-ignore
    return this.post('/hub', { query: qString });
};

const GetUniqueToken = function (this: APIWrapper, data: any): Promise<AxiosResponse<any, any>> {
    return this.post(ENV.API_BASE_URL + '/clients-dashboard/get-unique-token', data);
};

const GetFile = function (path: string, args: any, method = 'GET') {
    //@ts-ignore
    const baseUrl = this.getUri();
    const isGoApi = ENV.GO_API_BASE_URL === baseUrl;
    let req;

    if (path.match(/^http/)) {
        //@ts-ignore
        req = this.get(path, args);
    } else {
        if (!isGoApi) {
            //@ts-ignore
            req = this.get(baseUrl + path, args);
        } else {
            //@ts-ignore
            const params = method === 'GET' ? new URLSearchParams(args).toString() : '';
            //@ts-ignore
            const methodFn = method === 'GET' ? this.get : this.post;
            req = methodFn(`${baseUrl}${path}${params.length ? '?' + params : ''}`, method === 'POST' ? args : null, {
                responseType: 'blob'
            }).then((response: any) => {
                return response.data;
            });
        }
    }

    req.catch(function (xhr: any) {
        if (xhr.responseJSON && xhr.responseJSON.code == 403) {
            //if a 403 code is returned then probably the user session
            //has expired or the user lost his access
            //verifyValidSessionAndAccess();
        }
    });

    return req;
};

interface InstantFundingAPI extends AxiosInstance {
    graph(query: GraphQuery, ...args: GraphArg[]): Promise<AxiosResponse<any, any>>;
    getUniqueToken(data: any): Promise<AxiosResponse<any, any>>;
    getFile(path: string, params: any, method: string): Promise<AxiosResponse<any, any>>;
}

export type APIWrapper = AxiosInstance & InstantFundingAPI;

export function useAPI(viewId: string, useGoApi?: boolean) {
    const auth = useAuth();
    const controller = new AbortController();
    const abortControllerRun = useRef(false);
    const headers: any = {};

    //Authorization bearer token
    const bearer = window.sessionStorage.getItem(AUTH_BEARER_KEY);

    if (bearer?.length) {
        headers.Authorization = `Bearer ${bearer}`;
    }

    const api: APIWrapper = Object.assign(
        axios.create({
            withCredentials: true,
            baseURL: useGoApi ? ENV.GO_API_BASE_URL : ENV.API_BASE_URL,
            signal: controller.signal,
            // timeout: 1000,
            headers: headers
        }),
        { graph: Graph },
        { getUniqueToken: GetUniqueToken },
        { getFile: GetFile }
    );

    api.interceptors.response.use(
        function (response) {
            const data = response.data;
            if (
                data &&
                ((data.status === 'error' && data.code === 403) ||
                    (data.errors &&
                        find(data.errors, (e) => {
                            return e && (/OrbePublic/.exec(e.message) || /Cannot query field \"\w+\" on type \"Orbe\"/.exec(e.message));
                        })))
            ) {
                auth.checkSession?.();
            }
            return response;
        },
        function (error) {
            if ([401, 403].indexOf(error?.response?.status) !== -1) {
                //force to signout
                auth?.signout?.();
            }
            if (error && error.code === axios.AxiosError.ERR_CANCELED) {
                return Promise.reject(error);
            }
            // TODO: Do something with response error
            console.warn(`API::[${viewId}] Error in interceptor`, error);
            return Promise.reject(error);
        }
    );
    useEffect(() => {
        return () => {
            if (abortControllerRun.current) {
                return;
            }
            abortControllerRun.current = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return api as GraphAxios & InstantFundingAPI;
}

export function useRawAPI() {
    //Authorization bearer token
    const bearer = window.sessionStorage.getItem(AUTH_BEARER_KEY);
    const headers: any = {};
    if (bearer?.length) {
        headers.Authorization = `Bearer ${bearer}`;
    }
    const api = axios.create({
        withCredentials: true,
        baseURL: ENV.API_BASE_URL,
        headers: headers
    });
    return api;
}
